import React, { useState, useEffect } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import formatDate from '../../core/support/formatDate';
import { User } from '../../types/shared/User';
import LoadingIndicator from '../uiold/LoadingIndicator';
import { Text } from '../uiold';
import { themeColors } from '../uiold/Constants';

type Props = {
  users: Map<string, User> | null | undefined;
  selectedID: string | null | undefined;
  filterText: string;
  onItemClick: (id: string) => void;
  isSessionSystemAdmin: boolean;
};

function UsersList(props: Props) {
  let {
    users,
    selectedID,
    filterText,
    onItemClick,
    isSessionSystemAdmin,
  } = props;

  if (!users) {
    return <LoadingIndicator />;
  }

  let filteredUsers = filterUsers(users, filterText);
  let results = Array.from(filteredUsers, ([id, user]) => (
    <UsersListItem
      key={user.id}
      isSelected={id === selectedID}
      user={user}
      onPress={onItemClick}
      isSessionSystemAdmin={isSessionSystemAdmin}
    />
  ));
  return <>{results}</>;
}

function UsersListItem(props: {
  user: User;
  isSelected: boolean;
  onPress?: (userID: string) => void;
  isSessionSystemAdmin: boolean;
}) {
  let { user, isSelected, onPress, isSessionSystemAdmin } = props;
  // TODO: Only allow admin type users to create users
  let [userData, setUserData] = useState(user);
  useEffect(() => {
    setUserData(user);
  }, [user]);

  let isManager = user?.permissions?.users === 'WRITE';

  let rootStyle = [
    styles.root,
    isManager && styles.admin,
    isSelected && styles.selected,
    user && styles.suspended,
  ];
  return (
    <TouchableOpacity
      onPress={() => (onPress ? onPress(user?.id) : null)}
      style={rootStyle}
    >
      <Text style={styles.name}>{user.name}</Text>
      {user?.email ? <Text style={styles.text}>{user.email}</Text> : null}
      <View style={styles.usernameRow}>
        <Text style={styles.text}>
          {t('Username: {username}', { username: user.username })}
        </Text>
        {isManager ? (
          <Text style={styles.badge}>{t('Market Manager')}</Text>
        ) : null}
        {userData?.suspended ? (
          <Text style={[styles.badge, styles.suspendedBadge]}>
            {t('User Suspended')}
          </Text>
        ) : null}
      </View>
      {isSessionSystemAdmin ? (
        <Text style={styles.text}>
          {t('account_id (shows for sys_admin only) : {account}', {
            account: user.account,
          })}
        </Text>
      ) : null}
      <Text style={styles.text}>
        {t('Created: {createdAt}', { createdAt: formatDate(user.createdAt) })}
      </Text>
    </TouchableOpacity>
  );
}

function filterUsers(users: Map<string, User>, filterText: string) {
  let searchText = filterText.toLowerCase().trim();
  if (searchText === '') {
    return users;
  }
  let results = new Map();
  for (let user of users.values()) {
    if (userMatches(user, searchText)) {
      results.set(user?.id, user);
    }
  }
  return results;
}

function userMatches(user: User, searchText: string) {
  for (let key of ['name', 'username', 'email', 'account'] as const) {
    let value = typeof user[key] === 'string' ? user[key] : '';
    if (value && value.toLowerCase().includes(searchText)) {
      return true;
    }
  }
  return false;
}

let styles = StyleSheet.create({
  root: {
    padding: 15,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.lightGrey,
    backgroundColor: themeColors.white,
    cursor: 'pointer',
  },
  admin: {
    borderLeftColor: '#d05454',
  },
  suspended: { borderLeftColor: '#f4f407' },
  selected: {
    borderColor: themeColors.withOpacity.blue50,
    backgroundColor: themeColors.withOpacity.blue8,
  },
  name: {
    fontSize: 15,
    fontWeight: '500',
    color: '#2b4a5c',
    lineHeight: 22,
  },
  text: {
    fontSize: 13,
    color: '#577688',
    lineHeight: 22,
  },
  usernameRow: {
    flexDirection: 'row',
  },
  badge: {
    paddingHorizontal: 5,
    backgroundColor: themeColors.greenButtonHovered,
    fontSize: 11,
    color: themeColors.white,
    lineHeight: 22,
    marginHorizontal: 7,
  },
  suspendedBadge: { backgroundColor: '#f4f407', color: 'black' },
});

export default UsersList;
